import { __decorate } from "tslib";
import { BehaviorSubject, Subject } from "rxjs";
import { filter } from "rxjs/operators";
import { Alert, AlertType } from "./shared/models/alert";
import { Injectable, Injector, isDevMode } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import { SessionStorageService } from "./shared/services/storage/session-storage.service";
import { SessionStorageEnum } from "./shared/services/storage/session-storage-constants";
import { HttpStatusCodeEnum } from "./shared/models/http-status-code-enum.model";
import { SharedConstants } from "./shared/constants/shared.constants";
import { Title } from "@angular/platform-browser";
import { AppConfig } from "./app.config";
import { UserInformationService } from "./shared/services/user/user-information.service";
let AppControllerService = class AppControllerService {
    router;
    sessionStorage;
    titleService;
    injector;
    selectedApplicationID;
    selectedJobID;
    modules;
    organisations;
    appTitle;
    alertSubject = new Subject();
    keepAfterRouteChange = false;
    selectedOrgIdSubject = new BehaviorSubject(null);
    selectedOrgSubject = new BehaviorSubject(null);
    $selectedOrgSubject = this.selectedOrgSubject.asObservable();
    baseRequestObject = {
        authenticatedUser: {}, moduleID: null, appID: null
    };
    _selectedOrganisationID;
    configLoadedSubject = new BehaviorSubject(false);
    $configLoadedSubject = this.configLoadedSubject.asObservable();
    donNotReloadLastUrlOnRefreshObservableBehavior = new BehaviorSubject(false);
    $donNotReloadLastUrlOnRefreshObservable = this.donNotReloadLastUrlOnRefreshObservableBehavior.asObservable();
    moduleAllowResubmissionSubject = new BehaviorSubject(false);
    $moduleAllowResubmissionSubject = this.moduleAllowResubmissionSubject.asObservable();
    constructor(router, sessionStorage, titleService, injector) {
        this.router = router;
        this.sessionStorage = sessionStorage;
        this.titleService = titleService;
        this.injector = injector;
        // clear alert messages on route change unless 'keepAfterRouteChange' flag is true
        router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                if (this.keepAfterRouteChange) {
                    // only keep for a single route change
                    this.keepAfterRouteChange = false;
                }
                else {
                    // clear alert messages
                    // this.clearMessage();
                }
            }
        });
    }
    _selectedModuleID;
    get selectedModuleID() {
        const doubleQuoteRe = new RegExp("\"", "gi");
        if (this._selectedModuleID) {
            return this._selectedModuleID.toString().replace(doubleQuoteRe, "");
        }
        return this.sessionStorage.get(SessionStorageEnum.SelectedModuleID)?.replace(doubleQuoteRe, "");
    }
    get selectedOrganisationId() {
        return this.selectedOrgIdSubject.value;
    }
    get moduleAllowResubmission() {
        return this.moduleAllowResubmissionSubject.value;
    }
    selectedOrganisationID(organisationId) {
        this.selectedOrgIdSubject.next(organisationId);
        this.organisations.forEach(org => {
            if (org.id === organisationId) {
                this.selectedOrgSubject.next(org);
            }
        });
    }
    configLoadedComplete(complete) {
        this.configLoadedSubject.next(complete);
        this.titleService.setTitle(AppConfig.settings.projectSettings.title);
        this.appTitle = AppConfig.settings.projectSettings.projectName;
    }
    getRequest(includeAppID = true, includeModuleID = true) {
        const request = JSON.parse(JSON.stringify(this.baseRequestObject));
        if (includeAppID) {
            request.moduleID = this._selectedModuleID;
            request.AppID = this.selectedApplicationID;
        }
        else if (includeModuleID) {
            request.moduleID = this._selectedModuleID;
        }
        return request;
    }
    // subscribe to alerts
    getAlert(alertId) {
        return this.alertSubject.asObservable().pipe(filter((x) => x && x.alertId === alertId));
    }
    showMessage(message, type, keepAfterRouteChange = false) {
        const alert = new Alert({ message, type });
        this.keepAfterRouteChange = alert.keepAfterRouteChange = keepAfterRouteChange;
        this.alertSubject.next(alert);
    }
    // clear alerts
    clearMessage(alertId) {
        this.alertSubject.next(new Alert({ alertId }));
    }
    showConsoleMessage(message) {
        if (!isDevMode()) {
            console.log(message);
        }
    }
    setSelectedModule(value) {
        if (!value) {
            return;
        }
        if (this._selectedModuleID !== value) {
            this._selectedModuleID = value;
            this.sessionStorage.set(SessionStorageEnum.SelectedModuleID, value.toString());
        }
    }
    setModuleAllowResubmission() {
        const userInformationService = this.injector.get(UserInformationService);
        if (this._selectedModuleID !== undefined && this._selectedModuleID !== null) {
            const module = userInformationService.getModuleInformation(this._selectedModuleID);
            this.moduleAllowResubmissionSubject.next(module.enableResubmission);
        }
    }
    handleServerErrorMessage(objectName, httpErrorResponse, operation) {
        switch (httpErrorResponse.status) {
            case +HttpStatusCodeEnum.InternalServerError: {
                this.showMessage(objectName ? `Something went wrong ${operation} the ${objectName.toLocaleLowerCase()}` : "Something went wrong, please try again", AlertType.Error);
                break;
            }
            case +HttpStatusCodeEnum.BadRequest: {
                if (httpErrorResponse.error !== null) {
                    if (httpErrorResponse.error.hasOwnProperty("value") && httpErrorResponse.error.value !== undefined) {
                        this.showMessage(httpErrorResponse.error.value, AlertType.Warning);
                    }
                    else if (httpErrorResponse.error.hasOwnProperty("error") && httpErrorResponse.error.error.hasOwnProperty("message") && httpErrorResponse.error.error.message !== undefined) {
                        this.showMessage(httpErrorResponse.error.error.message, AlertType.Warning);
                    }
                    else if (httpErrorResponse.error.hasOwnProperty("error") && httpErrorResponse.error.error.hasOwnProperty("value") && httpErrorResponse.error.error.value !== undefined) {
                        this.showMessage(httpErrorResponse.error.error.value, AlertType.Warning);
                    }
                    else if (httpErrorResponse.error.hasOwnProperty("value") && httpErrorResponse.error.hasOwnProperty("message") && httpErrorResponse.error.error.message !== undefined) {
                        this.showMessage(httpErrorResponse.error.error.message, AlertType.Warning);
                    }
                    else if (httpErrorResponse.error.hasOwnProperty("errors") && httpErrorResponse.error.errors !== undefined) {
                        try {
                            const errors = [];
                            const keys = Object.keys(httpErrorResponse.error.errors);
                            keys.forEach(key => {
                                const error = httpErrorResponse.error.errors[key];
                                error.forEach(errorValue => {
                                    errors.push(errorValue);
                                });
                            });
                            this.showMessage(errors.join(" "), AlertType.Warning);
                        }
                        catch (e) {
                            // this should never swollow errors
                            this.showMessage(httpErrorResponse.message, AlertType.Warning);
                        }
                    }
                    else {
                        this.showMessage(httpErrorResponse.error, AlertType.Warning);
                    }
                }
                else {
                    this.showMessage(httpErrorResponse.message, AlertType.Warning);
                }
                break;
            }
            case +HttpStatusCodeEnum.Forbidden: {
                this.showMessage(`${SharedConstants.FORBIDDEN_MESSAGE}`, AlertType.Warning);
                break;
            }
            case +HttpStatusCodeEnum.NotFound: {
                this.showMessage(httpErrorResponse.message ? httpErrorResponse.message : `${SharedConstants.NOT_FOUND_MESSAGE}`, AlertType.Warning);
                break;
            }
            case +HttpStatusCodeEnum.Conflict: {
                this.showMessage(httpErrorResponse.message ? httpErrorResponse.message : `${SharedConstants.CONFLICT_MESSAGE}`, AlertType.Warning);
                break;
            }
            default: {
                if (objectName !== undefined && objectName !== null && objectName !== "") {
                    throw new Error(`${SharedConstants.UNKNOWN_ERROR_MESSAGE} ${operation} the ${objectName.toLocaleLowerCase()}: `
                        + httpErrorResponse.message);
                }
                else {
                    throw new Error(`${SharedConstants.UNKNOWN_ERROR_MESSAGE}: ` + httpErrorResponse.message);
                }
                break;
            }
        }
    }
    donNotReloadLastUrlOnRefresh(value) {
        this.donNotReloadLastUrlOnRefreshObservableBehavior.next(value);
    }
    static ctorParameters = () => [
        { type: Router },
        { type: SessionStorageService },
        { type: Title },
        { type: Injector }
    ];
};
AppControllerService = __decorate([
    Injectable()
], AppControllerService);
export { AppControllerService };
